import * as React from "react";
import {useState} from "react";
import {Box, Button, Container, Flex, HStack, Link, Stack, Text} from "@chakra-ui/react";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import SoundSpotsLogo from "../components/branding/SoundSpotsLogo";
import InstallAppDialog from "../components/appDownload/InstallAppDialog";

const T = {
    bg: "#FFFFFF",
    border: "#E0D4BC",
    ink: "#1E160A",
    inkMid: "#5A3E22",
    inkFaint: "#C8B090"
};

const LandingPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const areaId = searchParams.get("areaID") || searchParams.get("areaId");
    const appLink = areaId ? `/app?areaID=${areaId}` : "/app";
    const [isInstallDialogOpen, setInstallDialogOpen] = useState<boolean>(false);

    return (
        <Box minH={"100dvh"} maxH={"100dvh"} overflowY={"auto"} bg={T.bg}>
            <Box
                as={"nav"}
                position={"sticky"}
                top={0}
                zIndex={20}
                bg={T.bg}
                borderBottom={`1px solid ${T.border}`}
            >
                <Container maxW={"7xl"} px={{base: 5, md: 8}}>
                    <Flex height={"56px"} align={"center"} justify={"space-between"}>
                        <SoundSpotsLogo size={18} color={T.ink} accent={"#A0622A"}/>
                        <Link as={RouterLink} to={"/spaces"} fontSize={"12px"} color={T.inkMid} fontWeight={600} letterSpacing={"0.04em"}>
                            Public spaces
                        </Link>
                    </Flex>
                </Container>
            </Box>

            <Container maxW={"6xl"} py={{base: 10, md: 16}} height={"100%"}>
                <Flex direction={"column"} align={"center"} justify={"center"} minH={"70vh"}>
                    <Box maxW={"lg"} w={"full"}>
                        <Stack spacing={6} align={"flex-start"}>
                            <Text
                                color={"#A0622A"}
                                fontFamily={"Georgia, 'Times New Roman', serif"}
                                fontSize={{base: "22px", sm: "26px", md: "30px"}}
                                fontWeight={400}
                                letterSpacing={"-0.01em"}
                            >
                                Manage your SoundSpots installations
                            </Text>
                            <Text fontSize={{base: "md", md: "lg"}} color={T.inkMid}>
                                SoundSpots is a location-triggered audio platform for Android.
                                Visitors move through a physical space and hear contextual audio
                                automatically — no interaction required. You configure the experience
                                here and push it to devices in the field.
                            </Text>
                            <Stack spacing={3} fontSize={"sm"} color={T.inkMid}>
                                <Text>- Configure spaces and spots with BLE beacon, WiFi, or GPS triggers</Text>
                                <Text>- Upload and organise sound files, set trigger rules and layered ambience</Text>
                                <Text>- Publish versioned configurations and distribute to any number of device</Text>
                                <Text>- Full multi-language support within a single space — no duplicate setups</Text>
                            </Stack>
                            <Stack direction={{base: "column", sm: "row"}} spacing={3} align={"stretch"} w={"full"}>
                                <Button as={RouterLink} to={appLink} colorScheme={"teal"} size={"md"}>
                                    Open the app
                                </Button>
                                <Button as={RouterLink} to={"/spaces"} variant={"outline"} size={"md"}>
                                    Browse public spaces
                                </Button>
                            </Stack>
                            <Text fontSize={"xs"} color={T.inkFaint}>
                                Access requires an organization account. You can review the privacy policy before signing in.
                            </Text>
                            <HStack spacing={4} fontSize={"sm"} flexWrap={"wrap"} rowGap={2}>
                                <Link as={RouterLink} to={"/privacy"} color={"teal.600"}>
                                    Privacy policy
                                </Link>
                                <Link as={RouterLink} to={"/support"} color={"teal.600"}>
                                    Support
                                </Link>
                                <Link color={"teal.600"} onClick={() => setInstallDialogOpen(true)}>
                                    Install listener app
                                </Link>
                            </HStack>
                        </Stack>
                    </Box>
                </Flex>
            </Container>
            <InstallAppDialog isOpen={isInstallDialogOpen} onClose={() => setInstallDialogOpen(false)}/>
        </Box>
    );
};

export default LandingPage;
